import { defineStore } from 'pinia';

import api from './api';
import { FeatureFlag, type FeatureFlagValue } from './flags';

export { FeatureFlag, type FeatureFlagValue };

const useFeatureFlags = defineStore('featureFlags', () => {
  const flags = ref<Partial<Record<FeatureFlagValue, { enabled: boolean }>>>({});
  const hasSetFlagsForContext = ref<'' | 'auth' | 'public'>('');

  // Will initialise this plugins default flags by calling the GET /flags API endpoint.
  // Any overrides persisted to localStorage will be set too.
  async function setFlags(isAuthenticated: boolean) {
    if (isAuthenticated && hasSetFlagsForContext.value === 'auth') return;
    if (!isAuthenticated && hasSetFlagsForContext.value === 'public') return;

    let res;
    if (isAuthenticated) {
      res = await api.fetchUserFlags();
      hasSetFlagsForContext.value = 'auth';
    } else {
      res = await api.fetchPublicFlags();
      hasSetFlagsForContext.value = 'public';
    }
    flags.value = res.data.flags;
  }

  function getStatus(flag: FeatureFlagValue) {
    return flags.value[flag]?.enabled || false;
  }

  async function setOverride(flag: FeatureFlagValue, enabled: boolean) {
    const res = await api.setOverrideForSelf(flag, enabled);
    if (res.status === 200) {
      flags.value[flag] = { enabled };
    }
  }

  // Clear on org switch so the next setFlags() refetches the new org's flags.
  function reset() {
    flags.value = {};
    hasSetFlagsForContext.value = '';
  }

  return {
    setFlags,
    flags,
    ff: getStatus,
    setOverride,
    reset,
  };
});

export default useFeatureFlags;
